import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  Badge,
  Modal,
} from "react-bootstrap";
import {
  FaBriefcase,
  FaMapMarkerAlt,
  FaClock,
  FaTrash,
  FaPlusCircle,
  FaEnvelope,
  FaUserShield,
  FaPaperPlane,
  FaUpload,
} from "react-icons/fa";
import api from "../api";
import { AuthContext } from "../context/AuthContext";

const EMPLOYMENT_TYPES = [
  "Full-Time",
  "Part-Time",
  "Contract",
  "Internship",
  "Temporary",
  "Remote",
  "Other",
];

const defaultFormState = {
  title: "",
  department: "",
  location: "",
  employmentType: "Full-Time",
  salaryRange: "",
  description: "",
  responsibilities: "",
  requirements: "",
  applyEmail: "",
  applyLink: "",
};

const splitLines = (value) =>
  (typeof value === "string" ? value.split(/\r?\n/) : [])
    .map((item) => item.trim())
    .filter(Boolean);

export default function Careers() {
  const { user } = useContext(AuthContext);
  const isAdmin = Boolean(user?.isAdmin);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState(defaultFormState);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const buildApplyFormDefaults = useCallback(
    () => ({
      name: user?.username || "",
      email: user?.email || "",
      phone: user?.phone || "",
      message: "",
    }),
    [user?.username, user?.email, user?.phone]
  );
  const [selectedJob, setSelectedJob] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyForm, setApplyForm] = useState(buildApplyFormDefaults);
  const [applyError, setApplyError] = useState("");
  const [applySuccess, setApplySuccess] = useState("");
  const [applySubmitting, setApplySubmitting] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const resumeInputRef = useRef(null);

  const fetchJobs = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/careers");
      if (res.data?.success) {
        setJobs(Array.isArray(res.data.jobs) ? res.data.jobs : []);
      } else {
        setError(res.data?.message || "Failed to load job openings.");
        setJobs([]);
      }
    } catch (err) {
      console.error("Load careers error:", err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to load job openings."
      );
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setForm(defaultFormState);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isAdmin) {
      setError("Only administrators can post job openings.");
      return;
    }

    const trimmedTitle = form.title.trim();
    const trimmedDescription = form.description.trim();

    if (!trimmedTitle || !trimmedDescription) {
      setError("Title and description are required.");
      return;
    }

    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const payload = {
        adminEmail: user.email,
        title: trimmedTitle,
        department: form.department.trim() || undefined,
        location: form.location.trim() || undefined,
        employmentType: form.employmentType,
        salaryRange: form.salaryRange.trim() || undefined,
        description: trimmedDescription,
        responsibilities: splitLines(form.responsibilities),
        requirements: splitLines(form.requirements),
        applyEmail: form.applyEmail.trim() || undefined,
        applyLink: form.applyLink.trim() || undefined,
      };

      const res = await api.post("/careers", payload);
      if (res.data?.success && res.data?.job) {
        setJobs((prev) => [res.data.job, ...prev]);
        setSuccess("Job posting published successfully.");
        resetForm();
      } else {
        setError(res.data?.message || "Failed to create job posting.");
      }
    } catch (err) {
      console.error("Create job posting error:", err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to create job posting."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (jobId) => {
    if (!isAdmin || !jobId) {
      return;
    }
    const confirmed = window.confirm("Remove this job posting?");
    if (!confirmed) {
      return;
    }

    setDeletingId(jobId);
    setError("");
    setSuccess("");

    try {
      const res = await api.delete(`/careers/${jobId}`, {
        data: { adminEmail: user.email },
      });
      if (res.data?.success) {
        setJobs((prev) => prev.filter((job) => job._id !== jobId));
        setSuccess("Job posting removed.");
      } else {
        setError(res.data?.message || "Failed to remove job posting.");
      }
    } catch (err) {
      console.error("Delete job posting error:", err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to remove job posting."
      );
    } finally {
      setDeletingId("");
    }
  };

  const visibleJobs = useMemo(() => (Array.isArray(jobs) ? jobs : []), [jobs]);

  useEffect(() => {
    if (showApplyModal) {
      setApplyForm((prev) => ({
        ...prev,
        name: user?.username || prev.name,
        email: user?.email || prev.email,
        phone: user?.phone || prev.phone,
      }));
    } else {
      setApplyForm(buildApplyFormDefaults());
    }
  }, [showApplyModal, buildApplyFormDefaults]);

  const openApplyModal = (job) => {
    setSelectedJob(job);
    setApplyForm(buildApplyFormDefaults());
    setApplyError("");
    setApplySuccess("");
    setResumeFile(null);
    if (resumeInputRef.current) {
      resumeInputRef.current.value = "";
    }
    setShowApplyModal(true);
  };

  const closeApplyModal = () => {
    if (applySubmitting) {
      return;
    }
    setShowApplyModal(false);
    setSelectedJob(null);
    setApplyError("");
    setApplySuccess("");
    setResumeFile(null);
    if (resumeInputRef.current) {
      resumeInputRef.current.value = "";
    }
  };

  const handleApplyFieldChange = (event) => {
    const { name, value } = event.target;
    setApplyForm((prev) => ({ ...prev, [name]: value }));
    setApplyError("");
    setApplySuccess("");
  };

  const handleResumeChange = (event) => {
    const file =
      event.target.files && event.target.files[0]
        ? event.target.files[0]
        : null;
    setResumeFile(file);
    setApplyError("");
    setApplySuccess("");
  };

  const isValidEmail = (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || "");

  const handleApplySubmit = async (event) => {
    event.preventDefault();
    if (!selectedJob) {
      setApplyError("No job selected. Please try again.");
      return;
    }

    const name = applyForm.name.trim();
    const email = applyForm.email.trim();
    const phone = applyForm.phone.trim();
    const message = applyForm.message.trim();

    if (!name) {
      setApplyError("Please enter your full name.");
      return;
    }

    if (!email || !isValidEmail(email)) {
      setApplyError("Please provide a valid email address.");
      return;
    }

    if (!resumeFile) {
      setApplyError("Please attach your resume (PDF or Word).");
      return;
    }

    const formData = new FormData();
    formData.append("name", name);
    formData.append("email", email);
    if (phone) {
      formData.append("phone", phone);
    }
    if (message) {
      formData.append("message", message);
    }
    formData.append("resume", resumeFile);

    setApplySubmitting(true);
    setApplyError("");
    setApplySuccess("");

    try {
      const res = await api.post(
        `/careers/${selectedJob._id}/apply`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (res.data?.success) {
        setApplySuccess(
          res.data?.message ||
            "Application submitted successfully. You'll hear from us soon."
        );
        setResumeFile(null);
        if (resumeInputRef.current) {
          resumeInputRef.current.value = "";
        }
      } else {
        setApplyError(
          res.data?.message ||
            "Unable to submit application. Please try again later."
        );
      }
    } catch (err) {
      console.error("Submit job application error:", err);
      setApplyError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Unable to submit application. Please try again later."
      );
    } finally {
      setApplySubmitting(false);
    }
  };

  return (
    <Container className="py-4">
      <Row className="g-4">
        <Col lg={isAdmin ? 7 : 12}>
          <Card className="shadow-sm border-0 rounded-4 mb-4">
            <Card.Body>
              <div className="d-flex align-items-center gap-2 mb-3">
                <FaBriefcase className="text-primary" size={22} />
                <div>
                  <h4 className="mb-0 fw-bold">Open Positions</h4>
                  <small className="text-muted">
                    Explore career opportunities at Rentalo
                  </small>
                </div>
              </div>

              {error && (
                <Alert
                  variant="danger"
                  onClose={() => setError("")}
                  dismissible
                  className="mb-3"
                >
                  {error}
                </Alert>
              )}
              {success && (
                <Alert
                  variant="success"
                  onClose={() => setSuccess("")}
                  dismissible
                  className="mb-3"
                >
                  {success}
                </Alert>
              )}

              {loading ? (
                <div className="text-center my-5">
                  <Spinner animation="border" />
                </div>
              ) : visibleJobs.length === 0 ? (
                <div className="text-center text-muted my-5">
                  <FaBriefcase size={52} className="mb-3" />
                  <div className="fw-semibold">
                    No open positions right now.
                  </div>
                  <div>Check back soon for new opportunities.</div>
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {visibleJobs.map((job) => {
                    const responsibilities = Array.isArray(job.responsibilities)
                      ? job.responsibilities
                      : [];
                    const requirements = Array.isArray(job.requirements)
                      ? job.requirements
                      : [];
                    const createdDate = job.createdAt
                      ? new Date(job.createdAt)
                      : null;

                    return (
                      <Card key={job._id} className="border-0 shadow-sm">
                        <Card.Body>
                          <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3 mb-3">
                            <div>
                              <h5 className="mb-1 fw-bold d-flex align-items-center gap-2">
                                {job.title}
                                {job.employmentType && (
                                  <Badge
                                    bg="primary"
                                    className="text-uppercase"
                                  >
                                    {job.employmentType}
                                  </Badge>
                                )}
                              </h5>
                              <div className="text-muted small d-flex flex-wrap gap-3 align-items-center">
                                {job.department && (
                                  <span className="d-inline-flex align-items-center gap-1">
                                    <FaUserShield className="text-secondary" />
                                    {job.department}
                                  </span>
                                )}
                                {job.location && (
                                  <span className="d-inline-flex align-items-center gap-1">
                                    <FaMapMarkerAlt className="text-secondary" />
                                    {job.location}
                                  </span>
                                )}
                                {createdDate &&
                                  !Number.isNaN(createdDate.getTime()) && (
                                    <span className="d-inline-flex align-items-center gap-1">
                                      <FaClock className="text-secondary" />
                                      Posted {createdDate.toLocaleDateString()}
                                    </span>
                                  )}
                              </div>
                            </div>
                            {isAdmin && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDelete(job._id)}
                                disabled={deletingId === job._id}
                              >
                                {deletingId === job._id ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <span className="d-inline-flex align-items-center gap-2">
                                    <FaTrash /> Remove
                                  </span>
                                )}
                              </Button>
                            )}
                          </div>
                          <p className="text-secondary mb-3">
                            {job.description}
                          </p>

                          {responsibilities.length > 0 && (
                            <div className="mb-3">
                              <h6 className="fw-semibold mb-2">
                                Responsibilities
                              </h6>
                              <ul className="text-secondary small mb-0">
                                {responsibilities.map((item, index) => (
                                  <li key={index}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {requirements.length > 0 && (
                            <div className="mb-3">
                              <h6 className="fw-semibold mb-2">Requirements</h6>
                              <ul className="text-secondary small mb-0">
                                {requirements.map((item, index) => (
                                  <li key={index}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          <div className="d-flex flex-column flex-lg-row gap-2 align-items-start align-items-lg-center justify-content-between">
                            <div className="text-muted small d-flex flex-column gap-1">
                              {job.salaryRange && (
                                <span>Salary: {job.salaryRange}</span>
                              )}
                              {job.applyEmail && (
                                <span className="d-inline-flex align-items-center gap-1">
                                  <FaEnvelope className="text-secondary" />
                                  {job.applyEmail}
                                </span>
                              )}
                            </div>
                            {!isAdmin && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => openApplyModal(job)}
                              >
                                <FaPaperPlane className="me-2" /> Apply Now
                              </Button>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    );
                  })}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {isAdmin && (
          <Col lg={5}>
            <Card className="shadow-sm border-0 rounded-4">
              <Card.Body>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <FaPlusCircle className="text-success" size={20} />
                  <h5 className="mb-0 fw-bold">Post a new job</h5>
                </div>
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Job title</Form.Label>
                    <Form.Control
                      name="title"
                      value={form.title}
                      onChange={handleFieldChange}
                      placeholder="Senior Backend Engineer"
                      required
                    />
                  </Form.Group>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Department</Form.Label>
                        <Form.Control
                          name="department"
                          value={form.department}
                          onChange={handleFieldChange}
                          placeholder="Engineering"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Location</Form.Label>
                        <Form.Control
                          name="location"
                          value={form.location}
                          onChange={handleFieldChange}
                          placeholder="Bengaluru / Remote"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="g-3 mt-0">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Employment Type</Form.Label>
                        <Form.Select
                          name="employmentType"
                          value={form.employmentType}
                          onChange={handleFieldChange}
                        >
                          {EMPLOYMENT_TYPES.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Salary Range</Form.Label>
                        <Form.Control
                          name="salaryRange"
                          value={form.salaryRange}
                          onChange={handleFieldChange}
                          placeholder="₹12-18 LPA"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mt-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="description"
                      value={form.description}
                      onChange={handleFieldChange}
                      placeholder="Tell candidates about this role and why they'll love working with us"
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mt-3">
                    <Form.Label>Key Responsibilities</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="responsibilities"
                      value={form.responsibilities}
                      onChange={handleFieldChange}
                      placeholder="Enter one responsibility per line"
                    />
                  </Form.Group>
                  <Form.Group className="mt-3">
                    <Form.Label>Requirements</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="requirements"
                      value={form.requirements}
                      onChange={handleFieldChange}
                      placeholder="Enter one requirement per line"
                    />
                  </Form.Group>
                  <Row className="g-3 mt-0">
                    <Col md={6}>
                      <Form.Group className="mt-3 mt-md-0">
                        <Form.Label>Apply Email</Form.Label>
                        <Form.Control
                          name="applyEmail"
                          value={form.applyEmail}
                          onChange={handleFieldChange}
                          placeholder="careers@rentalo.com"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mt-3 mt-md-0">
                        <Form.Label>Apply Link</Form.Label>
                        <Form.Control
                          name="applyLink"
                          value={form.applyLink}
                          onChange={handleFieldChange}
                          placeholder="https://jobs.rentalo.com/123"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <div className="d-grid mt-4">
                    <Button type="submit" disabled={submitting}>
                      {submitting ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <span className="d-inline-flex align-items-center gap-2">
                          <FaPlusCircle /> Publish Job
                        </span>
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>

      <Modal
        show={showApplyModal}
        onHide={closeApplyModal}
        centered
        backdrop="static"
      >
        <Form onSubmit={handleApplySubmit}>
          <Modal.Header closeButton={!applySubmitting}>
            <Modal.Title>
              Apply for {selectedJob?.title || "this position"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {applyError && (
              <Alert
                variant="danger"
                onClose={() => setApplyError("")}
                dismissible
              >
                {applyError}
              </Alert>
            )}
            {applySuccess && (
              <Alert
                variant="success"
                onClose={() => setApplySuccess("")}
                dismissible
              >
                {applySuccess}
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Full name</Form.Label>
              <Form.Control
                name="name"
                value={applyForm.name}
                onChange={handleApplyFieldChange}
                placeholder="Your name"
                required
              />
            </Form.Group>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={applyForm.email}
                    onChange={handleApplyFieldChange}
                    placeholder="you@example.com"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Phone (optional)</Form.Label>
                  <Form.Control
                    name="phone"
                    value={applyForm.phone}
                    onChange={handleApplyFieldChange}
                    placeholder=""
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mt-3">
              <Form.Label>Cover letter / message</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="message"
                value={applyForm.message}
                onChange={handleApplyFieldChange}
                placeholder="Tell us why you'd be a great fit"
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>Resume (PDF or Word)</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleResumeChange}
                ref={resumeInputRef}
                required
              />
              <Form.Text muted>
                <FaUpload className="me-2" /> Max 5 MB. Accepted formats: PDF,
                DOC, DOCX.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={closeApplyModal}
              disabled={applySubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={applySubmitting}>
              {applySubmitting ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <span className="d-inline-flex align-items-center gap-2">
                  <FaPaperPlane /> Submit Application
                </span>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}
